/**
 * onePagerStatus.ts
 * Single source of truth for KPI cell status (colour) in the One Pager.
 *
 * The BACKEND owns all threshold logic — see
 * `docs/one-pager-color-coding-contract.md`. The API decides *whether* a KPI is
 * meeting its target; this module only translates that decision into a boolean
 * the UI can style from. No thresholds, no directions, no scale maths here.
 *
 * Consumed by the table rows, the grand total row, the mobile card view and the
 * Excel export so all four surfaces cannot drift apart.
 */

import { GroupedRow } from '../constants/onePagerConstants';
import type { OnePagerFlag } from '../types/onePagerTypes';

// ─── Flag interpretation ──────────────────────────────────────────────────────

/**
 * Whether a tri-state backend flag means "not meeting target".
 *
 * Only an explicit `true` is red. `false` (meeting target), `null` (no target
 * configured / no actual) and `undefined` (key absent) are all "not red".
 *
 * The strict `=== true` comparison is deliberate:
 *  - It never coerces `null` to red. `null` means *unknown*, not *bad*.
 *  - It is immune to the backend later adding `@JsonInclude(NON_NULL)`, which
 *    would turn a `null` flag into an absent key (`undefined`).
 */
export const isFlagBad = (flag: OnePagerFlag): boolean => flag === true;

// ─── Cell status ──────────────────────────────────────────────────────────────

/**
 * Whether a KPI cell should render in the danger (red) colour.
 *
 * Returns false when the row has no actual value for the column: a cell showing
 * '—' must never be painted red, since there is no measurement to fail against.
 * This mirrors the backend's "no target configured / no actual → null" rule and
 * matters today for Undirected Hours %, whose flag currently mirrors Overtime's
 * while the metric itself is not yet returned.
 */
export const isMetricBad = (row: GroupedRow | null | undefined, columnId: string): boolean => {
	if (!row) return false;

	const value = row.vals?.[columnId];
	const hasActual = typeof value === 'number' && !isNaN(value);
	if (!hasActual) return false;

	return isFlagBad(row.flags?.[columnId]);
};
